import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Paper,
  MenuItem,
  styled,
} from '@mui/material';

export const StyledFormControl = styled(FormControl, {
  shouldForwardProp: prop =>
    prop !== 'noBorder' &&
    prop !== 'isPrimaryBorder' &&
    prop !== 'formControlHeight' &&
    prop !== 'formControlHeightMobile',
})(
  ({
    theme,
    noBorder: __,
    isPrimaryBorder: _,
    formControlHeight = 55,
    formControlHeightMobile = 55,
  }) => ({
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    minHeight: formControlHeight,
    height: formControlHeight,

    [theme.breakpoints.down('sm')]: {
      minHeight: formControlHeightMobile,
      height: formControlHeightMobile,
    },

    '&:focus-within label': {
      color: theme.primary,
    },
  })
);

export const StyledInputLabel = styled(InputLabel, {
  shouldForwardProp: prop =>
    prop !== 'isFloating' && prop !== 'isPrimaryBorder',
})(({ theme, isFloating, isPrimaryBorder }) => {
  const floatingColor = isPrimaryBorder ? theme.primary : theme.tertiary;

  const floatingHoverColor = isPrimaryBorder ? theme.tertiary : theme.primary;

  const defaultColor = theme.palette.text.textGray;

  return {
    zIndex: 1,
    overflow: 'visible',
    whiteSpace: 'normal',
    maxWidth: 'calc(100% - 32px)',
    fontSize: { xs: 16, md: 14, lg: 16 },
    fontFamily: 'Open Sans, sans-serif',
    fontWeight: 400,
    lineHeight: 1.4375,
    position: 'absolute',
    top: isFloating ? -10 : '50%',
    left: 12,
    transform: isFloating ? 'scale(0.75)' : 'translateY(-50%)',
    transformOrigin: 'top left',
    pointerEvents: 'none',
    userSelect: 'none',
    backgroundColor: isFloating ? '#fff' : 'transparent',
    paddingLeft: isFloating ? theme.spacing(0.5) : 0,
    paddingRight: isFloating ? theme.spacing(0.5) : 0,
    transition: 'color 0.2s, transform 0.2s',
    color: isFloating ? floatingColor : defaultColor,

    '&:hover': {
      color: isFloating ? floatingHoverColor : defaultColor,
    },

    '&.Mui-focused': {
      color: isPrimaryBorder ? theme.tertiary : theme.primary,
    },
  };
});

export const StyledButtonWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'noBorder' && prop !== 'isPrimaryBorder',
})(({ theme, noBorder, isPrimaryBorder }) => ({
  height: '100%',
  display: 'flex',
  borderRadius: '8px',
  boxSizing: 'border-box',
  transition: 'border-color 0.2s',
  border: noBorder ? 'none' : '1px solid',
  borderColor: noBorder
    ? 'transparent'
    : isPrimaryBorder
    ? theme.primary
    : theme.tertiary,
  '&:hover': {
    borderColor: noBorder
      ? 'transparent'
      : isPrimaryBorder
      ? theme.tertiary
      : theme.primary,
  },
}));

export const StyledButton = styled(Button)(() => ({
  height: '100%',
  width: '100%',
  padding: 0,
  paddingLeft: 14,
  paddingRight: 14,
  display: 'flex',
  textTransform: 'none',
  fontFamily: 'Open Sans, sans-serif',
  alignItems: 'center',
  justifyContent: 'flex-start',
  whiteSpace: 'normal',
  overflow: 'visible',
  textAlign: 'left',
  boxSizing: 'border-box',
}));

// styles.js
export const menuItemContentStyles = (
  theme,
  menuItemHeight = 50,
  mainColorInHover = false
) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  p: 0,
  minHeight: menuItemHeight,
  height: 'auto',
  width: '100%',
  flexShrink: 1,
  fontFamily: 'Open Sans, sans-serif',
  whiteSpace: 'normal',
  overflow: 'visible',
  wordBreak: 'break-word',
  color: theme.text2,
  transition: 'color 0.2s ease, background-color 0.2s ease',
  '&:hover': {
    backgroundColor: mainColorInHover ? theme.palette.primary.main : 'auto',
    borderRadius: '8px',
    '&, & *': {
      color: mainColorInHover ? theme.text1 : theme.text2,
      transition: 'color 0.2s ease, filter 0.2s ease',
    },
    '& img': {
      filter: mainColorInHover ? 'brightness(0) invert(1)' : 'none',
    },
  },
});

export const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: prop =>
    prop !== 'mainColorInHover' && prop !== 'menuItemHeight',
})(({ theme, mainColorInHover, menuItemHeight = 50 }) => ({
  ...menuItemContentStyles(theme, menuItemHeight, mainColorInHover),
  id: 'styled-menu-item',
  p: 1,
}));

export const StyledPaper = styled(Paper, {
  shouldForwardProp: prop => prop !== 'autoWidth',
})(({ autoWidth }) => ({
  position: 'absolute',
  top: 'calc(100% + 2px)',
  left: 0,
  width: autoWidth ? 'max-content' : '100%',
  maxHeight: 300,
  overflowY: 'auto',
  zIndex: 999999,
  backgroundColor: '#f5f5f5',
}));
